import React from "react";
import { useTranslation } from "react-i18next";
import AdminMobileTopbar from "./AdminMobileTopbar";
import { useContactSearch } from "./useContactSearch";

const MobileContactSearch = () => {
  const { t } = useTranslation();
  const {
    clients,
    clientId,
    setClientId,
    urlsText,
    setUrlsText,
    formError,
    stage,
    job,
    selectedIds,
    toggleSelected,
    pushMessage,
    discardMessage,
    progressError,
    isStarting,
    isPushing,
    isDiscarding,
    pushSucceeded,
    urlCount,
    handleStart,
    handlePush,
    handleDiscard,
    successResults,
    failedResults,
    progressPct,
    MAX_URLS,
  } = useContactSearch();

  return (
    <div className="flex flex-col h-screen bg-secondaryBg">
      <AdminMobileTopbar title={t("admin.contactSearch.head", "Búsqueda de contactos")} />

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {stage === "form" && (
          <>
            <div className="mb-3">
              <label className="block text-primary text-xs font-bold mb-1">{t("admin.option.selectClient")} *</label>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full bg-white text-primary text-sm rounded-lg px-3 py-2.5 border border-gray-200"
              >
                <option value="">{t("button.selectOption")}</option>
                {clients.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-2">
              <label className="block text-primary text-xs font-bold mb-1">
                {t("admin.contactSearch.urlsLabel", "URLs (una por línea)")}
              </label>
              <textarea
                value={urlsText}
                onChange={(e) => setUrlsText(e.target.value)}
                rows={10}
                placeholder="https://empresa1.com&#10;https://empresa2.com"
                className="w-full bg-white text-primary text-xs rounded-lg px-3 py-2.5 border border-gray-200"
              />
              <p className="text-[10px] text-gray-400 mt-1">{urlCount} / {MAX_URLS}</p>
            </div>

            {formError && <p className="text-red-500 text-xs mb-3">{formError}</p>}

            <button
              type="button"
              onClick={handleStart}
              disabled={isStarting}
              className="w-full bg-secondary text-white text-sm font-extrabold rounded-xl py-3 disabled:opacity-70"
            >
              {isStarting ? t("admin.contactSearch.starting", "Iniciando...") : t("admin.contactSearch.startBtn", "Buscar")}
            </button>
          </>
        )}

        {stage === "progress" && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
              <div className="bg-secondary h-2.5 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
            </div>
            <p className="text-sm text-primary font-bold">{progressPct}%</p>
            <p className="text-xs text-gray-400 mt-1">
              {job?.processed || 0} / {job?.total || 0}
            </p>
            {progressError && <p className="text-red-500 text-xs mt-4 text-center">{progressError}</p>}
          </div>
        )}

        {stage === "results" && (
          <>
            {pushMessage && (
              <div className={`rounded-lg p-3 mb-3 text-xs ${pushSucceeded ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {pushMessage}
              </div>
            )}
            {discardMessage && (
              <div className="bg-gray-100 rounded-lg p-3 mb-3 text-xs text-gray-600">{discardMessage}</div>
            )}

            <p className="text-[11px] text-gray-500 mb-2">
              {t("admin.contactSearch.successCount", "Exitosos")}: {successResults.length} · {t("admin.contactSearch.failedCount", "Fallidos")}: {failedResults.length}
            </p>

            <div className="flex flex-col gap-2 mb-4">
              {successResults.map((r) => (
                <label key={r.id} className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggleSelected(r.id)}
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-[12px] font-bold text-primary m-0 truncate">{r.company || r.url}</p>
                    <p className="text-[10px] text-gray-400 m-0 truncate">{r.email || r.url}</p>
                  </div>
                </label>
              ))}
              {failedResults.map((r) => (
                <div key={r.id} className="bg-red-50 rounded-xl border border-red-100 p-3">
                  <p className="text-[11px] text-red-600 m-0 truncate">{r.url}</p>
                </div>
              ))}
            </div>

            {!pushSucceeded && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleDiscard}
                  disabled={isDiscarding}
                  className="flex-1 text-xs font-bold text-primary bg-white border border-gray-300 rounded-lg py-3 disabled:opacity-70"
                >
                  {t("admin.contactSearch.discardBtn", "Descartar")}
                </button>
                <button
                  type="button"
                  onClick={handlePush}
                  disabled={isPushing || selectedIds.size === 0}
                  className="flex-1 text-xs font-bold text-white bg-secondary rounded-lg py-3 disabled:opacity-70"
                >
                  {isPushing ? t("admin.contactSearch.pushing", "Enviando...") : t("admin.contactSearch.pushBtn", "Enviar")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MobileContactSearch;
